import { createContext, useCallback, useContext, type ReactNode } from "react"
import { services } from "@/services"
import fallbackJson from "@/services/static/data/conteudo-site.json"
import type { SiteContent } from "@/services/types"
import { useResource } from "@/shared/hooks/useResource"

/* ─────────────────────────────────────────────────────────────────────────────
 * Conteudo editorial do site, carregado uma unica vez.
 *
 * Oito componentes precisam desse conteudo. Se cada um chamasse o service, hoje
 * seriam oito leituras de JSON (irrelevante) e amanha oito requisicoes HTTP em
 * paralelo para montar uma landing page (nao irrelevante).
 * ────────────────────────────────────────────────────────────────────────── */

const SiteContentContext = createContext<SiteContent | null>(null)

/**
 * Conteudo embutido no bundle.
 *
 * Um site institucional nao pode ficar em branco porque a API de conteudo caiu.
 * Se a fonte remota falhar, a pagina segue com esta copia — desatualizada e
 * melhor que ausente.
 */
const FALLBACK_CONTENT = fallbackJson as SiteContent

function loadSiteContent(): Promise<SiteContent> {
  return services.content.getSiteContent()
}

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-off-white" role="status" aria-live="polite">
      <span className="sr-only">Carregando</span>
      <span
        className="w-8 h-8 rounded-full border-2 border-verde-profundo/20 border-t-verde-profundo animate-spin"
        aria-hidden="true"
      />
    </div>
  )
}

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const loader = useCallback(loadSiteContent, [])
  const { data, loading, error } = useResource(loader)

  if (loading) return <Splash />

  const content = data ?? FALLBACK_CONTENT

  if (error) {
    console.error("Falha ao carregar o conteudo do site; usando o conteudo do bundle.", error)
  }

  return <SiteContentContext value={content}>{children}</SiteContentContext>
}

export function useSiteContent(): SiteContent {
  const content = useContext(SiteContentContext)

  if (!content) {
    throw new Error("useSiteContent precisa estar dentro de <SiteContentProvider>.")
  }

  return content
}
